import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { Observable, of, delay } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { SharedApiService, ApiType } from '../../../../../libs/shared/src/lib/services/api.service';
import { MarketPredictionsResponse } from '../models/market-predictions.models';

/**
 * Market Predictions Service
 * Handles API calls for AI-powered market predictions
 */
@Injectable({
  providedIn: 'root',
})
export class MarketPredictionsService {
  private readonly isServer: boolean;

  constructor(
    private sharedApiService: SharedApiService,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isServer = isPlatformServer(platformId);
  }

  /**
   * Get latest market predictions
   * Endpoint: /api/analytics/predictions/latest
   */
  getLatestPredictions(): Observable<MarketPredictionsResponse | null> {
    // If running on server, return mock data
    if (this.isServer) {
      return of(null).pipe(delay(100));
    }

    return this.sharedApiService
      .get<any>('api/analytics/predictions/latest', undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // Backend wraps response in { body: {...}, message: "OK", status: 200 }
          const data = response?.body || response;

          // Validate data has expected structure
          if (!data || data.status !== 'completed') {
            console.warn('Predictions not ready or invalid response:', response);
            return null;
          }
          return data as MarketPredictionsResponse;
        }),
        catchError((error) => {
          console.error('Error fetching market predictions:', error);
          return of(null);
        })
      );
  }

  /**
   * Get predictions for a specific month
   * @param month - Month in format "YYYY-MM"
   */
  getPredictionsForMonth(month: string): Observable<MarketPredictionsResponse | null> {
    // If running on server, return mock data
    if (this.isServer) {
      return of(null).pipe(delay(100));
    }

    return this.sharedApiService
      .get<any>(`api/analytics/predictions/${month}`, undefined, {
        apiType: ApiType.OPEN,
        jobsRequest: true,
      })
      .pipe(
        map((response) => {
          // Backend wraps response in { body: {...}, message: "OK", status: 200 }
          const data = response?.body || response;

          if (!data || data.status !== 'completed') {
            console.warn('Predictions not ready or invalid response:', response);
            return null;
          }
          return data as MarketPredictionsResponse;
        }),
        catchError((error) => {
          console.error(`Error fetching predictions for ${month}:`, error);
          return of(null);
        })
      );
  }
}
